// 0.47.96 - First: the base path (the site under another site's path).
import './lib/basePath';
// 0.49.61 - The first requests, started at once (after the base path).
import './lib/bootstrap';
// 0.48.22 - Armenian "և" in capitals as "ԵՎ" (lib/armenianCaps.js).
import './lib/armenianCaps';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
// 0.44.75 - Applies the last known system name and icon before the first paint.
import './lib/systemIdentity';
// 0.46.78 - And the last known button and focus colors.
import { applyCachedUiTheme } from './lib/uiTheme';
import { watchPageWidth } from './lib/pageWidth';
import App from './App';
// 0.49.61 - The site's fonts, served with the site (were Google Fonts).
// Only the alphabets a page uses are downloaded (unicode-range).
import '@fontsource/noto-sans-armenian/400.css';
import '@fontsource/noto-sans-armenian/500.css';
import '@fontsource/noto-sans-armenian/600.css';
import '@fontsource/noto-sans-armenian/700.css';
import '@fontsource/noto-sans/400.css';
import '@fontsource/noto-sans/500.css';
import '@fontsource/noto-sans/600.css';
import '@fontsource/noto-sans/700.css';
import './styles/global.css';

applyCachedUiTheme();
// 0.47.17 - The page's width without the scrollbar, for full-width bands.
watchPageWidth();

createRoot(document.getElementById('root')).render(
  <StrictMode><App /></StrictMode>
);

/**
 * 0.38.47 - Runtime cache-bust check.
 *
 * Problem being solved:
 *   Browsers apply heuristic freshness to any HTML response with no
 *   Cache-Control header (roughly 10% of the Last-Modified age). A
 *   stale index.html reference to old hashed /assets/*.js means a
 *   deploy takes hours or days to reach citizens even though the
 *   new files are already on disk. Hard-reload sometimes bypasses,
 *   sometimes doesn't. Incognito always fetches fresh, which is why
 *   the mismatch was so obvious in testing.
 *
 * Solution:
 *   Every `vite build` bakes a unique __BUILD_ID__ into the bundle
 *   and writes dist/version.json with the same id (see vite.config.js).
 *   On app load we fetch /version.json with a cache-busting query
 *   string. If the server's buildId differs from what's in the
 *   currently-loaded bundle, the browser is running stale HTML +
 *   stale JS. We navigate to a fresh URL (adds ?v=<serverBuildId>)
 *   which the browser has never seen - it fetches a new index.html
 *   from the server, which points at the current hashed asset URLs,
 *   and the app reloads on the new version. One redirect, no user
 *   action needed.
 *
 * Works with:
 *   - Any nginx / CDN cache configuration (including "no headers set"
 *     which is what caused the original bug on caf.govtech.am)
 *   - Any browser (uses standard fetch + location APIs; no service
 *     worker, no storage, no permissions)
 *   - Any deploy: the check happens on every page load, so future
 *     ships propagate on the next citizen navigation
 *
 * Guards:
 *   - Only reloads ONCE per stale-detection: we stamp
 *     sessionStorage.gt18_reloaded_for_build with the new id, so if
 *     the server is briefly serving inconsistent files across a
 *     partial deploy, we don't loop.
 *   - Silent on any fetch failure (offline, /version.json 404,
 *     malformed JSON) - the app still runs; the check just skips.
 *   - `__BUILD_ID__` is a Vite `define` constant that becomes a
 *     string literal at build time. In dev (`vite serve`) it's still
 *     defined but points at the dev server's start time, which does
 *     nothing useful because /version.json isn't served in dev - the
 *     fetch 404s and the check silently skips. Fine.
 */
(function checkForStaleBundle() {
  // eslint-disable-next-line no-undef
  const currentBuildId = typeof __BUILD_ID__ !== 'undefined' ? __BUILD_ID__ : null;
  if (!currentBuildId) return;
  // 0.38.49 - Expose the baked-in build id as a real window property
  // for runtime diagnostics. `__BUILD_ID__` itself is a Vite `define`
  // string-substitution and doesn't exist as a global at runtime, so
  // Console can't inspect it directly. Setting `window.__GT18_BUILD_ID__`
  // to the substituted value gives future me (or admin) a way to
  // confirm which bundle is loaded: paste `window.__GT18_BUILD_ID__`
  // into Console and compare against `/version.json`.
  try { window.__GT18_BUILD_ID__ = currentBuildId; } catch (_e) { /* ignore */ }
  fetch(`/version.json?_=${Date.now()}`, { cache: 'no-store' })
    .then((r) => (r.ok ? r.json() : null))
    .then((data) => {
      if (!data || typeof data.buildId !== 'string') return;
      if (data.buildId === currentBuildId) return;
      // Stale bundle detected. Prevent loop if a second stale-check
      // fires before the reload completes (e.g. a partial deploy).
      const key = 'gt18_reloaded_for_build';
      let lastReloadedFor = null;
      try { lastReloadedFor = sessionStorage.getItem(key); } catch (_e) { /* private mode */ }
      if (lastReloadedFor === data.buildId) return;
      try { sessionStorage.setItem(key, data.buildId); } catch (_e) { /* ignore */ }
      // Navigate to a fresh URL so the browser cannot reuse a cached
      // index.html response. Any pathname is fine as long as the
      // query differs from anything the browser has stored.
      const u = new URL(window.location.href);
      u.searchParams.set('v', data.buildId);
      window.location.replace(u.toString());
    })
    .catch(() => { /* silent - offline, 404, etc. */ });
})();
